import dayjs, { Dayjs } from "dayjs";

export type PresetDef = { label: string; start: () => Dayjs; end: () => Dayjs };

export type PresetKey = "7D" | "thisMonth" | "lastMonth" | "30D" | "90D" | "12M" | "custom";

export const presets: Record<PresetKey, PresetDef> = {
  "7D": { label: "7 Days", start: () => dayjs.utc().subtract(7, "day"), end: () => dayjs.utc() },
  "thisMonth": { label: "This Month", start: () => dayjs.utc().startOf("month"), end: () => dayjs.utc().endOf("month") },
  "30D": { label: "30 Days", start: () => dayjs.utc().subtract(30, "day"), end: () => dayjs.utc() },
  "lastMonth": { label: "Last Month", start: () => dayjs.utc().subtract(1, "month").startOf("month"), end: () => dayjs.utc().subtract(1, "month").endOf("month") },
  "90D": { label: "90 Days", start: () => dayjs.utc().subtract(90, "day"), end: () => dayjs.utc() },
  "12M": { label: "12 Months", start: () => dayjs.utc().subtract(12, "month"), end: () => dayjs.utc() },
  custom: { label: "Custom", start: () => dayjs.utc().subtract(30, "day"), end: () => dayjs.utc() },
};

// Dashboard tabs share the `range`/`start`/`end` query params but not the same preset map
// (a tab may use its own map). A `range` key from another tab arrives paired with that
// tab's `start`/`end`, so resolve all three together: an unrecognised key means the dates
// belong to someone else too, and both fall back to this tab's default preset. Otherwise
// the dropdown shows one range while the pickers show another.
export function resolveRangeFromParams<K extends string>(
  params: URLSearchParams,
  presetMap: Record<K, PresetDef>,
  fallback: K,
): { preset: K; start: Dayjs; end: Dayjs } {
  const rangeParam = params.get("range");
  const known = rangeParam !== null && rangeParam in presetMap;
  const preset = known ? (rangeParam as K) : fallback;

  const startParam = known ? params.get("start") : null;
  const endParam = known ? params.get("end") : null;

  return {
    preset,
    start: startParam && dayjs.utc(startParam).isValid() ? dayjs.utc(startParam) : presetMap[preset].start(),
    end: endParam && dayjs.utc(endParam).isValid() ? dayjs.utc(endParam) : presetMap[preset].end(),
  };
}
